import { useEffect } from 'react';
import { InvoiceService } from 'api';
import { AutocompletePaginationAsync } from 'components/Inputs';
import { CorrectiveInvoice, Invoice } from 'pages/invoices/invoices.types';
import { FieldPath, FieldValues } from 'react-hook-form';
import { InvoicesParams } from 'types/invoices';
import { createHandleOnBlur, handleChange } from 'utils/form.utils';
import { RenderProps } from './types';

type Props<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  formProps: RenderProps<TFieldValues, TName>;
  isEditing?: boolean;
  invoiceData?: Invoice | CorrectiveInvoice;
  options: Invoice[];
  params: InvoicesParams;
  setParams: React.Dispatch<React.SetStateAction<InvoicesParams>>;
  setOptions: React.Dispatch<React.SetStateAction<Invoice[]>>;
  error?: boolean;
};

export default function CorrectedInvoiceAutocomplete<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  formProps,
  isEditing,
  invoiceData,
  options,
  setOptions,
  params,
  setParams,
  error,
}: Props<TFieldValues, TName>) {
  const {
    getInvoices,
    loading: invoicesLoading,
    invoicesData,
  } = InvoiceService.useGetInvoices();

  useEffect(() => {
    if (invoicesData?.invoices.length) {
      setOptions((prev) => [...prev, ...invoicesData.invoices]);
    }
  }, [invoicesData?.invoices]);

  useEffect(() => {
    getInvoices({ ...params });
  }, [getInvoices, params]);

  return (
    <AutocompletePaginationAsync
      {...formProps}
      padding='dense'
      width={280}
      loading={invoicesLoading}
      error={error}
      placeholder={
        isEditing && invoiceData
          ? (invoiceData as CorrectiveInvoice).correctedInvoiceCode
          : undefined
      }
      disabled={isEditing}
      options={options}
      getOptionLabel={(option) => option.code}
      handleSearchChange={handleChange(setOptions, setParams)}
      onScrollBottom={() =>
        setParams((prev) => ({
          ...prev,
          pageNumber: (prev.pageNumber += 1),
        }))
      }
      onBlur={createHandleOnBlur(setOptions, setParams, formProps.field.onBlur)}
      onChange={(_, data) => formProps.field.onChange(data)}
    />
  );
}
